import type { DocumentStructureSortBy } from "@msuite/utils/documents";
import { useMemo } from "react";
import { Checkbox } from "../../checkbox";
import { useTranslation } from "../../localization";
import { useFileExplorerContext } from "./context";
import { FileExplorerHeaderItem } from "./file-explorer-header-item";
import { checkItemsState, handleBatchSelectedFiles } from "./shared";

export function FileExplorerHeader() {
	/** Context */
	const {
		selectedFiles,
		setSelectedFiles,
		structure,
		sortByDirection,
		setSortBy,
		setSortByDirection,
		sortBy,
		selectColumns,
	} = useFileExplorerContext();

	/** Hooks */
	const { t } = useTranslation();

	/** Memoized */
	const state = useMemo(() => {
		return checkItemsState(structure?.items || [], selectedFiles);
	}, [selectedFiles, structure]);

	/** Functions */
	function handleOnCheckboxToggle() {
		const newSelectedFiles = handleBatchSelectedFiles(state, structure?.items || [], selectedFiles);
		setSelectedFiles(newSelectedFiles);
	}

	function handleOnHeaderClick(name: DocumentStructureSortBy) {
		if (sortBy === name) {
			setSortByDirection(sortByDirection === "asc" ? "desc" : "asc");
		} else {
			setSortBy(name);
			setSortByDirection("asc");
		}
	}

	/** Render */
	return (
		<div className="file-explorer-header z-[2] sticky top-0">
			<FileExplorerHeaderItem className="file-explorer-checkbox-column">
				<Checkbox
					value={state !== "unchecked"}
					isIndeterminate={state === "indeterminate"}
					onChange={handleOnCheckboxToggle}
				/>
			</FileExplorerHeaderItem>
			<FileExplorerHeaderItem
				className="file-explorer-name-column"
				onClick={() => {
					handleOnHeaderClick("name");
				}}
				name="name"
			>
				{t("files.file_name")}
			</FileExplorerHeaderItem>
			{selectColumns?.map((column) => (
				<FileExplorerHeaderItem
					key={column.key}
					className="file-explorer-select-column"
				>
					{column.header}
				</FileExplorerHeaderItem>
			))}
			<FileExplorerHeaderItem
				className="file-explorer-size-column"
				onClick={() => {
					handleOnHeaderClick("size");
				}}
				name="size"
			>
				{t("files.file_size")}
			</FileExplorerHeaderItem>
			<FileExplorerHeaderItem className="file-explorer-created-at-column">
				{t("general_fields.created_at")}
			</FileExplorerHeaderItem>
		</div>
	);
}
